/**
 * Storico delle conversazioni (V59): l'elenco delle proprie e la singola
 * conversazione a tutta pagina.
 *
 * Si vedono solo le proprie, come sul server, e si eliminano davvero: niente
 * cestino, quindi la conferma dice cosa sta per sparire. La conversazione
 * riaperta e la stessa del pannello (V58), non una copia: tornando a una
 * vista e riaprendo il pannello si continua da dove si era arrivati.
 *
 * L'elenco puo diventare lungo: il server lo consegna a pagine di dieci, con
 * la ricerca nel titolo, la squadra e il periodo, tutti nella query string
 * come negli altri elenchi.
 *
 * Il contesto delle domande, qui, e la squadra in cui la conversazione e
 * nata: il percorso `/assistente` non ne porta una.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ClockIcon,
  MessageCircleQuestionIcon,
  MessagesSquareIcon,
  Minimize2Icon,
  ScrollTextIcon,
  SearchIcon,
  ShieldIcon,
  SparklesIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  PERIODI_CONVERSAZIONI,
  type PeriodoConversazioni,
  RIGHE_CONVERSAZIONI,
  SENZA_SQUADRA,
} from '@calcio/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { chiama } from '../api/client.js';
import { AzioniTestata, ComandoIcona, MenuAzioni, PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { Filtro, Riepilogo, TUTTI, VoceRiepilogo } from '../comuni/FiltriVista.js';
import { usePagine } from '../comuni/Paginazione.js';
import { numeroPagine } from '../comuni/pagine.js';
import { formatoIstante } from '../comuni/date.js';
import { messaggio, useDati } from '../comuni/dati.js';
import {
  type ConversazioneLetta,
  type PaginaConversazioni,
  type VoceConversazione,
  useAssistente,
} from './contesto.js';
import { useContestoDomanda, useNuovaConversazione } from './contestoDomanda.js';
import { Dialogo } from './Dialogo.js';
import { useIndietro } from '../navigazione/indietro.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const formatoQuando = formatoIstante({
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "17/09/2026, 18:30" nell'ora italiana. */
const quando = (istante: string): string => formatoQuando.format(new Date(istante));

const titoloDi = (c: { titolo: string | null }): string => c.titolo ?? 'Conversazione senza titolo';

const squadraDi = (c: { squadra: { nome: string; stagione: string } | null }): string =>
  c.squadra ? `${c.squadra.nome} · ${c.squadra.stagione}` : 'Nessuna squadra';

const domandeDi = (n: number): string => `${n} ${n === 1 ? 'domanda' : 'domande'}`;

const ETICHETTA_PERIODO: Record<PeriodoConversazioni, string> = {
  settimana: 'Ultima settimana',
  mese: 'Ultimo mese',
  trimestre: 'Ultimi tre mesi',
};

/** I filtri che vanno al server; la pagina la legge la paginazione. */
const FILTRI = ['q', 'squadra', 'periodo'] as const;

/** Il tempo di una battuta: la ricerca parte quando si smette di scrivere. */
const ATTESA_RICERCA = 300;

export function Conversazioni() {
  const [params, setParams] = useSearchParams();
  const { apri, conversazione, dimentica } = useAssistente();
  const [pagine, setPagine] = useState<number | null>(null);
  const { pagina, barra, inizio } = usePagine(pagine);

  const query = new URLSearchParams();
  for (const f of FILTRI) {
    const v = params.get(f);
    if (v) query.set(f, v);
  }
  if (pagina > 1) query.set('pagina', String(pagina));
  const filtrato = FILTRI.some((f) => params.has(f));
  // Tra un filtro e l'altro resta a schermo l'elenco di prima, senza salti.
  const { dati, errore, ricarica } = useDati<PaginaConversazioni>(
    `/assistente/conversazioni${query.size ? `?${query.toString()}` : ''}`,
    { tieni: true },
  );

  useEffect(() => {
    if (dati) setPagine(numeroPagine(dati.totale, RIGHE_CONVERSAZIONI));
  }, [dati]);

  const [cerca, setCerca] = useState(params.get('q') ?? '');
  const [daEliminare, setDaEliminare] = useState<VoceConversazione | null>(null);

  const imposta = (chiave: string, valore: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (valore && valore !== TUTTI) next.set(chiave, valore);
        else next.delete(chiave);
        next.delete('pagina');
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    const t = setTimeout(() => {
      if (cerca.trim() !== (params.get('q') ?? '')) imposta('q', cerca.trim());
    }, ATTESA_RICERCA);
    return () => clearTimeout(t);
    // Solo il testo scritto fa ripartire l'attesa, non l'URL che cambia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cerca]);

  const nuova = () => {
    dimentica();
    apri();
  };

  const elimina = async (c: VoceConversazione) => {
    try {
      await chiama(`/assistente/conversazioni/${c.id}`, { metodo: 'DELETE' });
      // Se era quella aperta nel pannello, il pannello riparte da vuoto.
      if (conversazione.id === c.id) dimentica();
      await ricarica();
      toast.success('Conversazione eliminata.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setDaEliminare(null);
    }
  };

  const testata = (
    <Testata
      titolo="Conversazioni"
      voce="conversazioni"
      accanto={
        dati &&
        (dati.totale > 0 || filtrato) && (
          <Riepilogo>
            <VoceRiepilogo
              Icona={MessagesSquareIcon}
              valore={dati.totale}
              titolo={filtrato ? 'Conversazioni con questi filtri' : 'Conversazioni'}
            />
          </Riepilogo>
        )
      }
      azione={<PulsanteNuovo etichetta="Nuova conversazione" onClick={nuova} />}
    />
  );

  if (!dati)
    return (
      <>
        {testata}
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );

  // Nessuna conversazione in assoluto: niente filtri, solo come si comincia.
  if (dati.totale === 0 && !filtrato)
    return (
      <>
        {testata}
        <Corpo>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <SparklesIcon />
              </EmptyMedia>
              <EmptyTitle>Nessuna conversazione</EmptyTitle>
              <EmptyDescription>
                L'assistente si apre da «Chiedi», sopra la vista che stai guardando: le domande che
                gli fai finiscono qui. Le conversazioni ferme da sei mesi si cancellano da sole.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={nuova}>
                <SparklesIcon data-icon="inline-start" />
                Apri l'assistente
              </Button>
            </EmptyContent>
          </Empty>
        </Corpo>
      </>
    );

  const squadra = params.get('squadra') ?? TUTTI;
  const periodo = params.get('periodo') ?? TUTTI;

  return (
    <>
      {testata}
      <Corpo>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <InputGroup className="w-full @xl:max-w-sm">
              <InputGroupInput
                placeholder="Cerca nel titolo"
                aria-label="Cerca nel titolo"
                value={cerca}
                onChange={(e) => setCerca(e.target.value)}
              />
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
            </InputGroup>
            <Filtro
              etichetta="Squadra"
              valore={squadra}
              onCambia={(v) => imposta('squadra', v)}
              opzioni={[
                { valore: TUTTI, etichetta: 'Tutte' },
                ...dati.squadre.map((s) => ({
                  valore: s.id,
                  etichetta: squadraDi({ squadra: s }),
                })),
                ...(dati.senzaSquadra || squadra === SENZA_SQUADRA
                  ? [{ valore: SENZA_SQUADRA, etichetta: 'Nessuna squadra' }]
                  : []),
              ]}
            />
            <Filtro
              etichetta="Ultimo messaggio"
              valore={periodo}
              onCambia={(v) => imposta('periodo', v)}
              opzioni={[
                { valore: TUTTI, etichetta: 'Qualsiasi' },
                ...PERIODI_CONVERSAZIONI.map((p) => ({
                  valore: p,
                  etichetta: ETICHETTA_PERIODO[p],
                })),
              ]}
            />
          </div>

          {dati.voci.length === 0 ? (
            <StatoVuoto titolo="Nessuna conversazione con questi filtri" />
          ) : (
            // Sotto la testata fissa del telefono, tornandoci cambiando pagina.
            <Card ref={inizio} className="scroll-mt-(--altezza-testata) bg-muted">
              <CardContent className="flex flex-col gap-4 px-2 @xl:px-(--card-spacing)">
                <ul className="flex flex-col gap-2">
                  {dati.voci.map((c) => (
                    <li key={c.id}>
                      <RigaConversazione
                        conversazione={c}
                        inCorso={conversazione.id === c.id}
                        onElimina={() => setDaEliminare(c)}
                      />
                    </li>
                  ))}
                </ul>
                {barra}
              </CardContent>
            </Card>
          )}

          <Nota>
            Una conversazione ferma da sei mesi si cancella da sola, con le domande e i dati letti.
          </Nota>
        </div>

        <ConfermaEliminazione
          conversazione={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          onConferma={() => (daEliminare ? elimina(daEliminare) : Promise.resolve())}
        />
      </Corpo>
    </>
  );
}

/**
 * Una conversazione dell'elenco, come le righe delle altre librerie: il
 * riquadro con l'icona, il titolo che apre la conversazione su tutta la
 * riga, sotto squadra, domande e ultimo messaggio. Il cestino le sta sopra.
 */
function RigaConversazione({
  conversazione: c,
  inCorso,
  onElimina,
}: {
  conversazione: VoceConversazione;
  /** E quella aperta nel pannello. */
  inCorso: boolean;
  onElimina: () => void;
}) {
  return (
    <div className="relative flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
      <span className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        <SparklesIcon aria-hidden className="size-5" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={`/assistente/${c.id}`}
            className="text-base font-semibold break-words after:absolute after:inset-0 after:rounded-lg"
          >
            {titoloDi(c)}
          </Link>
          {inCorso && <Badge variant="outline">Nel pannello</Badge>}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <ShieldIcon aria-hidden className="size-4" />
            {squadraDi(c)}
          </span>
          <span className="flex items-center gap-1" title="Domande fatte">
            <MessageCircleQuestionIcon aria-hidden className="size-4" />
            <span className="font-medium text-foreground tabular-nums">{c.domande}</span>
          </span>
          <span className="flex items-center gap-1" title="Ultimo messaggio">
            <ClockIcon aria-hidden className="size-4" />
            <span className="tabular-nums">{quando(c.aggiornatoIl)}</span>
          </span>
        </span>
      </span>

      <ComandoIcona
        etichetta={`Elimina «${titoloDi(c)}»`}
        Icona={Trash2Icon}
        size="icon-sm"
        className="relative inline-flex shrink-0"
        onClick={onElimina}
      />
    </div>
  );
}

function ConfermaEliminazione({
  conversazione,
  onChiudi,
  onConferma,
}: {
  conversazione: { titolo: string | null; domande: number } | null;
  onChiudi: () => void;
  onConferma: () => Promise<void>;
}) {
  return (
    <AlertDialog open={conversazione !== null} onOpenChange={(aperto) => !aperto && onChiudi()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare la conversazione?</AlertDialogTitle>
          <AlertDialogDescription>
            {conversazione &&
              `«${titoloDi(conversazione)}», ${domandeDi(conversazione.domande)}: sparisce con le risposte e i dati letti, e non si recupera.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => void onConferma()}>
            Elimina
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Una conversazione a tutta pagina: `/assistente/:id`, o `/assistente/nuova`
 * finché la prima domanda non le ha dato un id. Allora la pagina passa al
 * suo percorso, senza ricaricarla: la conversazione in memoria è già quella.
 */
export function PaginaConversazione() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { stato, conversazione, imposta, dimentica, apri, mostraLetture, impostaLetture } =
    useAssistente();
  const { contesto, nomeSquadra } = useContestoDomanda();
  const nuova = useNuovaConversazione();
  const torna = useIndietro('/');
  const { dati, errore } = useDati<ConversazioneLetta>(id ? `/assistente/conversazioni/${id}` : '');
  const [conferma, setConferma] = useState(false);

  // La conversazione letta diventa quella in corso, una volta per lettura:
  // da qui in poi pannello e pagina parlano della stessa. Se in memoria c'è
  // già, è la più fresca e resta.
  const applicata = useRef<ConversazioneLetta | null>(null);
  useEffect(() => {
    if (!dati || applicata.current === dati) return;
    applicata.current = dati;
    if (dati.id !== conversazione.id) {
      imposta({ id: dati.id, messaggi: dati.messaggi, squadra: dati.squadra });
    }
  }, [dati, conversazione.id, imposta]);

  // La nuova ha avuto il suo id con la prima domanda.
  useEffect(() => {
    if (!id && conversazione.id) {
      void navigate(`/assistente/${conversazione.id}`, { replace: true });
    }
  }, [id, conversazione.id, navigate]);

  // Aperta in memoria (la nuova, o quella appena creata) si mostra subito,
  // senza aspettare la lettura dal server.
  const inMemoria = !id || conversazione.id === id;
  const primaDomanda = conversazione.messaggi.find((m) => m.ruolo === 'user')?.testo;
  const titolo = dati
    ? titoloDi(dati)
    : inMemoria
      ? (primaDomanda ?? 'Nuova conversazione')
      : 'Conversazione';

  const nelPannello = () => {
    torna();
    apri();
  };

  const elimina = async () => {
    if (!conversazione.id) return;
    try {
      await chiama(`/assistente/conversazioni/${conversazione.id}`, { metodo: 'DELETE' });
      dimentica();
      toast.success('Conversazione eliminata.');
      void navigate('/assistente', { replace: true });
    } catch (e) {
      setConferma(false);
      toast.error(messaggio(e));
    }
  };

  const pronta = stato?.disponibile && (dati || inMemoria);
  const aperta = dati?.creatoIl ?? conversazione.messaggi[0]?.creatoIl;

  return (
    <>
      <Testata
        titolo={titolo}
        indietro="/assistente"
        sopra={pronta && (aperta ? `${nomeSquadra} · aperta il ${quando(aperta)}` : nomeSquadra)}
        azione={
          pronta && (
            <AzioniTestata>
              {conversazione.messaggi.length > 0 && (
                <PulsanteNuovo etichetta="Nuova conversazione" onClick={nuova} />
              )}
              <MenuAzioni
                voci={[
                  { etichetta: 'Nel pannello', Icona: Minimize2Icon, onSelect: nelPannello },
                  {
                    etichetta: mostraLetture ? 'Nascondi le letture' : 'Mostra le letture',
                    Icona: ScrollTextIcon,
                    onSelect: () => impostaLetture(!mostraLetture),
                  },
                  conversazione.id && {
                    etichetta: 'Elimina',
                    Icona: Trash2Icon,
                    onSelect: () => setConferma(true),
                    distruttiva: true,
                  },
                ]}
              />
            </AzioniTestata>
          )
        }
      />
      <Corpo>
        {!pronta ? (
          <StatoCaricamento
            errore={
              errore ?? (stato && !stato.disponibile ? "L'assistente non è disponibile." : null)
            }
          />
        ) : (
          <>
            {/* Altezza fissa: la conversazione scorre da sé, come nel pannello. */}
            <Card className="h-[70dvh] gap-0 overflow-hidden py-0">
              <Dialogo contesto={contesto} domande={stato.domande} />
            </Card>

            <ConfermaEliminazione
              conversazione={
                conferma
                  ? {
                      titolo,
                      domande: conversazione.messaggi.filter((m) => m.ruolo === 'user').length,
                    }
                  : null
              }
              onChiudi={() => setConferma(false)}
              onConferma={elimina}
            />
          </>
        )}
      </Corpo>
    </>
  );
}
